import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Card, CardActionArea, CardContent, CircularProgress, Chip } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ShieldIcon from '@mui/icons-material/Shield';
import FlagIcon from '@mui/icons-material/Flag';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { api } from '../../utils/api';

const TEAL = '#2D6E6A';
const AMBER = '#B26A00';
const RED = '#C62828';
const GREEN = '#2D6E6A';

type Tone = 'calm' | 'attention' | 'error';

interface Signal {
  loading: boolean;
  failed: boolean;
  value: string;     // big number / status text
  caption: string;   // sub-line
  tone: Tone;        // drives color
}

interface DashboardHomeTabProps {
  onNavigate?: (label: string, subTab?: number) => void;
}

const blank = (): Signal => ({ loading: true, failed: false, value: '—', caption: '', tone: 'calm' });

const toneColor = (t: Tone) => (t === 'error' ? RED : t === 'attention' ? AMBER : GREEN);

// A value is "numeric-ish" (short) vs a word like "Operational" — drives auto-shrink
// so long word-values don't clip in the 4-across desktop grid.
const isShortValue = (v: string) => v.length <= 6;

const DashboardHomeTab: React.FC<DashboardHomeTabProps> = ({ onNavigate }) => {
  const [support, setSupport] = useState<Signal>(blank());
  const [system, setSystem] = useState<Signal>(blank());
  const [access, setAccess] = useState<Signal>(blank());
  const [ai, setAi] = useState<Signal>(blank());
  const [plans, setPlans] = useState<Signal>(blank());
  const [safety, setSafety] = useState<Signal>(blank());
  const [moderation, setModeration] = useState<Signal>(blank());
  const [financial, setFinancial] = useState<Signal>(blank());

  const load = useCallback(() => {
    // Support — /admin/support/sla-status (counts come back as strings)
    (async () => {
      try {
        const res = await api.get('/admin/support/sla-status');
        const data = await res.json();
        const s = data.summary || data.sla || data;
        const open = parseInt(s?.open_count ?? '0', 10) || 0;
        const breached = parseInt(s?.breached_count ?? '0', 10) || 0;
        const urgent = parseInt(s?.urgent_open ?? '0', 10) || 0;
        setSupport({
          loading: false, failed: false,
          value: String(open),
          caption: open === 0 ? 'No open tickets' : `${breached} SLA-breached · ${urgent} urgent`,
          tone: breached > 0 || urgent > 0 ? 'error' : open > 0 ? 'attention' : 'calm',
        });
      } catch {
        setSupport({ loading: false, failed: true, value: '—', caption: 'Could not load support', tone: 'calm' });
      }
    })();

    // System — /admin/system-health (v1: DB connectivity)
    (async () => {
      try {
        const res = await api.get('/admin/system-health');
        const data = await res.json();
        const ok = data.status === 'operational';
        const failedChecks = (data.checks || []).filter((c: any) => !c.ok).map((c: any) => c.name);
        setSystem({
          loading: false, failed: false,
          value: ok ? 'Operational' : 'Degraded',
          caption: ok ? 'All checks passing' : `Issues: ${failedChecks.join(', ') || 'see system'}`,
          tone: ok ? 'calm' : 'error',
        });
      } catch {
        setSystem({ loading: false, failed: true, value: '—', caption: 'Could not load system health', tone: 'calm' });
      }
    })();

    // Access — pending early-access requests
    (async () => {
      try {
        const res = await api.get('/admin/users/access-requests?status=pending');
        const data = await res.json();
        const n = (data.requests || []).length;
        setAccess({
          loading: false, failed: false,
          value: String(n),
          caption: n === 0 ? 'No pending requests' : `${n} awaiting review`,
          tone: n > 0 ? 'attention' : 'calm',
        });
      } catch {
        setAccess({ loading: false, failed: true, value: '—', caption: 'Could not load access requests', tone: 'calm' });
      }
    })();

    // AI — error rate over last 7 days (matches AI Ops tab's 0.05 threshold)
    (async () => {
      try {
        const res = await api.get('/admin/ai-operations?days=7');
        const data = await res.json();
        const summary = data?.window?.summary || {};
        const errorRate: number = typeof summary.errorRate === 'number' ? summary.errorRate : 0;
        const rateLimited = summary.rateLimitedCount ?? 0;
        const warn = errorRate > 0.05 || rateLimited > 0;
        setAi({
          loading: false, failed: false,
          value: `${(errorRate * 100).toFixed(1)}%`,
          caption: warn ? `Elevated · ${rateLimited} rate-limited` : 'Error rate nominal',
          tone: errorRate > 0.05 ? 'error' : warn ? 'attention' : 'calm',
        });
      } catch {
        setAi({ loading: false, failed: true, value: '—', caption: 'Could not load AI metrics', tone: 'calm' });
      }
    })();

    // Plan Upgrades — pending plan-change requests. Same source the Plans tab uses
    // (PlanRequestsTab: GET /admin/users/plan-requests?status=pending).
    (async () => {
      try {
        const res = await api.get('/admin/users/plan-requests?status=pending');
        const data = await res.json();
        const n = (data.requests || []).length;
        setPlans({
          loading: false, failed: false,
          value: String(n),
          caption: n === 0 ? 'No pending upgrades' : `${n} awaiting decision`,
          tone: n > 0 ? 'attention' : 'calm',
        });
      } catch {
        setPlans({ loading: false, failed: true, value: '—', caption: 'Could not load plan requests', tone: 'calm' });
      }
    })();

    // Safety Reports — high-priority (CSAM/illegal) content reports. ANY open one is
    // the loudest thing on this page: always 'error' tone so it cannot be missed.
    // Source: GET /admin/content-reports?priority=high (same list endpoint the T&S tab uses).
    (async () => {
      try {
        const res = await api.get('/admin/content-reports?priority=high');
        const data = await res.json();
        const reports = data.reports || [];
        const open = reports.filter((r: any) => r.status === 'open' || r.status === 'reviewing').length;
        setSafety({
          loading: false, failed: false,
          value: String(open),
          caption: open === 0 ? 'No open safety reports' : `${open} need immediate review`,
          tone: open > 0 ? 'error' : 'calm',
        });
      } catch {
        setSafety({ loading: false, failed: true, value: '—', caption: 'Could not load safety reports', tone: 'calm' });
      }
    })();

    // Moderation — open NON-high-priority reports (routine queue). Filtered to
    // priority=normal so it does not double-count the Safety card's high-priority set.
    (async () => {
      try {
        const res = await api.get('/admin/content-reports?priority=normal');
        const data = await res.json();
        const reports = data.reports || [];
        const open = reports.filter((r: any) => r.status === 'open' || r.status === 'reviewing').length;
        setModeration({
          loading: false, failed: false,
          value: String(open),
          caption: open === 0 ? 'Queue clear' : `${open} awaiting review`,
          tone: open > 0 ? 'attention' : 'calm',
        });
      } catch {
        setModeration({ loading: false, failed: true, value: '—', caption: 'Could not load moderation queue', tone: 'calm' });
      }
    })();

    // Financial — monthly burn (the real figure the Financial tab leads with).
    // Source: GET /admin/cost-items -> summary.monthlyTotal; aws.total for caption.
    // Informational (calm) — not an "attention" signal.
    (async () => {
      try {
        const res = await api.get('/admin/cost-items');
        const data = await res.json();
        const monthly = data?.summary?.monthlyTotal ?? 0;
        const awsTotal = data?.aws?.total;
        const money = (n: number) => '$' + (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
        setFinancial({
          loading: false, failed: false,
          value: money(monthly),
          caption: typeof awsTotal === 'number' ? `AWS MTD ${money(awsTotal)}` : 'Monthly burn (normalized)',
          tone: 'calm',
        });
      } catch {
        setFinancial({ loading: false, failed: true, value: '—', caption: 'Could not load financials', tone: 'calm' });
      }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  // Order: safety first among the alertable signals, then support/system, then the rest.
  const cards: { key: string; title: string; icon: React.ReactNode; sig: Signal; target: string; subTab?: number }[] = [
    { key: 'safety', title: 'Safety Reports', icon: <ShieldIcon />, sig: safety, target: 'Trust & Safety' },
    { key: 'support', title: 'User Support', icon: <SupportAgentIcon />, sig: support, target: 'Support' },
    { key: 'system', title: 'System Health', icon: <MonitorHeartIcon />, sig: system, target: 'System', subTab: 3 },
    { key: 'ai', title: 'AI Warnings', icon: <AutoAwesomeIcon />, sig: ai, target: 'AI Ops' },
    { key: 'moderation', title: 'Moderation', icon: <FlagIcon />, sig: moderation, target: 'Moderation' },
    { key: 'access', title: 'Access Requests', icon: <HowToRegIcon />, sig: access, target: 'Access' },
    { key: 'plans', title: 'Plan Upgrades', icon: <WorkspacePremiumIcon />, sig: plans, target: 'Plans' },
    { key: 'financial', title: 'Financial', icon: <AttachMoneyIcon />, sig: financial, target: 'Financial' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h6" fontWeight={700}>What needs your attention</Typography>
        <Typography variant="body2" color="text.secondary">
          A live snapshot of safety, support, system health, and the rest of the platform. Tap a card to jump to that area.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
        {cards.map(({ key, title, icon, sig, target, subTab }) => {
          const color = toneColor(sig.tone);
          const flagged = sig.tone !== 'calm' && !sig.failed;
          return (
            <Card key={key} variant="outlined"
              sx={{ borderRadius: 3, borderColor: flagged ? color : '#E0ECEA',
                    borderWidth: flagged ? 2 : 1, position: 'relative',
                    transition: 'box-shadow .2s, transform .2s',
                    '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' } }}>
              <CardActionArea
                onClick={() => onNavigate?.(target, subTab)}
                aria-label={`Open ${target}`}
                sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent sx={{ p: 2.5, minHeight: 168, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Box sx={{ bgcolor: '#E8F0EE', borderRadius: '50%', width: 44, height: 44,
                               display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEAL }}>
                      {icon}
                    </Box>
                    {flagged && <Chip size="small" label={sig.tone === 'error' ? 'Urgent' : 'Attention'}
                      sx={{ bgcolor: sig.tone === 'error' ? '#FBE3E3' : '#FFF3E0', color, fontWeight: 600, height: 22 }} />}
                  </Box>

                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.3 }}>
                    {title}
                  </Typography>

                  {sig.loading ? (
                    <Box sx={{ py: 1.5 }}><CircularProgress size={24} sx={{ color: TEAL }} /></Box>
                  ) : (
                    <>
                      <Typography fontWeight={700}
                        sx={{ color, lineHeight: 1.15, my: 0.5,
                              fontSize: isShortValue(sig.value) ? '2.125rem' : '1.35rem',
                              overflowWrap: 'anywhere' }}>
                        {sig.value}
                      </Typography>
                      <Typography variant="body2" color={sig.failed ? 'error.main' : 'text.secondary'}
                        sx={{ minHeight: 20 }}>
                        {sig.caption}
                      </Typography>
                    </>
                  )}

                  <Box sx={{ flex: 1 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: TEAL }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                      Open {target}
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 15 }} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        System Health is a v1 connectivity check; richer monitoring (Lambda error rates, alarms) is planned. Financial shows normalized monthly burn.
      </Typography>
    </Box>
  );
};

export default DashboardHomeTab;
